import {quizActions, useQuiz} from "../../providers/QuizProvider.tsx";
import {useGeoSearch, useSearchBox} from "react-instantsearch";
import type {GeoHit} from "instantsearch.js";
import {type Practice} from "../PracticesMapContainer.tsx";
import {useEffect, useMemo, useRef, useState} from "react";
import {CheckCircle, List, Locate, Map as MapIcon, MapPin, Search} from "lucide-react";
import clsx from "clsx";
import {AdvancedMarker, Map, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import {metersToMiles} from "../../lib/utils.ts";
import {sortBy} from "lodash-es";

interface PracticeSelectorProps {
    name: string;
    label: string;
    required?: boolean;
}

const PracticeSelector = ({name, label, required = false}: PracticeSelectorProps) => {
    const {state, dispatch} = useQuiz();
    const selectedPractice = state.formData[name];

    const {items, refine: refineGeoSearch} = useGeoSearch<GeoHit<Practice>>();
    const {refine: refineSearchBox} = useSearchBox();
    const map = useMap();
    const geometry = useMapsLibrary('geometry');
    const places = useMapsLibrary('places');

    const [userLocation, setUserLocation] = useState<google.maps.places.PlaceResult | null>(null);
    const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({lat: 39.8283, lng: -98.5795}); // Center of US
    const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
    const [isGettingLocation, setIsGettingLocation] = useState(false);

    // Setup autocomplete
    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options: google.maps.places.AutocompleteOptions = {
            fields: ['geometry', 'formatted_address'],
            componentRestrictions: {country: ['US']}
        };

        setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            const place = placeAutocomplete.getPlace();
            setUserLocation(place);
            if (place.geometry?.location) {
                const newCenter = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                };
                setMapCenter(newCenter);
                map?.setCenter(newCenter);
                map?.setZoom(12);

                // Refine Algolia search to show practices near this location
                refineSearchBox(''); // Clear any text search
                refineGeoSearch({
                    northEast: {
                        lat: newCenter.lat + 0.5, // ~35 miles radius
                        lng: newCenter.lng + 0.5
                    },
                    southWest: {
                        lat: newCenter.lat - 0.5,
                        lng: newCenter.lng - 0.5
                    }
                });
            }
        });
    }, [placeAutocomplete, map, refineGeoSearch, refineSearchBox]);

    const sortedPractices = useMemo<GeoHit<Practice>[]>(() => {
        if (!geometry?.spherical || !userLocation?.geometry?.location) {
            return items;
        }
        return sortBy(items.map(item => ({
            ...item,
            _rankingInfo: {
                ...item._rankingInfo,
                // @ts-expect-error bug
                geoDistance: metersToMiles(geometry.spherical.computeDistanceBetween(item._geoloc, userLocation.geometry.location))
            }
        })), '_rankingInfo.geoDistance') as GeoHit<Practice>[];
    }, [geometry?.spherical, items, userLocation]);

    const handleSelectPractice = (practice: Practice | null) => {
        dispatch(quizActions.updateFormData({[name]: practice}));
        if (practice && map) {
            map.setCenter(practice._geoloc);
            map.setZoom(14);
        }
    };

    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setIsGettingLocation(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const {latitude, longitude} = position.coords;
                const newCenter = {lat: latitude, lng: longitude};

                // Create a mock PlaceResult for the user's location
                const mockPlace: google.maps.places.PlaceResult = {
                    geometry: {
                        location: new google.maps.LatLng(latitude, longitude)
                    } as google.maps.places.PlaceGeometry,
                    formatted_address: 'Your Current Location'
                };

                setUserLocation(mockPlace);
                setMapCenter(newCenter);
                map?.setCenter(newCenter);
                map?.setZoom(12);

                // Refine Algolia search to show practices near this location
                refineSearchBox('');
                refineGeoSearch({
                    northEast: {
                        lat: newCenter.lat + 0.5,
                        lng: newCenter.lng + 0.5
                    },
                    southWest: {
                        lat: newCenter.lat - 0.5,
                        lng: newCenter.lng - 0.5
                    }
                });

                // Update search input to show we're using current location
                if (inputRef.current) {
                    inputRef.current.value = 'Using your current location';
                }

                setIsGettingLocation(false);
            },
            (error) => {
                console.error('Error getting location:', error);
                let errorMessage = 'Unable to retrieve your location. ';

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage += 'Please enable location permissions in your browser.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage += 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage += 'Location request timed out.';
                        break;
                }

                alert(errorMessage);
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium mb-3">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {/* Search Box and View Switcher */}
            <div className="flex gap-3 mb-4">
                <div className="relative flex-1">
                    <input
                        ref={inputRef}
                        type="search"
                        autoComplete="off"
                        placeholder="Enter City, State or Zip to find nearby practices"
                        className="w-full border-2 border-gray-300 rounded-lg py-3 px-4 pl-11 pr-12 focus:outline-none focus:border-blue-500"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20}/>

                    {/* Use My Location Button */}
                    <button
                        type="button"
                        onClick={handleGetCurrentLocation}
                        disabled={isGettingLocation}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                        title="Use my current location"
                    >
                        <Locate size={20} className={isGettingLocation ? 'animate-pulse' : ''}/>
                    </button>
                </div>

                {/* View Mode Toggle */}
                <div className="flex border-2 border-gray-300 rounded-lg overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        className={clsx(
                            "px-4 py-3 flex items-center gap-2 transition-colors",
                            viewMode === 'list'
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-700 hover:bg-gray-100"
                        )}
                    >
                        <List size={20}/>
                        <span className="hidden sm:inline">List</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('map')}
                        className={clsx(
                            "px-4 py-3 flex items-center gap-2 transition-colors border-l-2 border-gray-300",
                            viewMode === 'map'
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-700 hover:bg-gray-100"
                        )}
                    >
                        <MapIcon size={20}/>
                        <span className="hidden sm:inline">Map</span>
                    </button>
                </div>
            </div>

            {/* Map View */}
            {viewMode === 'map' && (
                <div className="h-[350px] md:h-[500px] rounded-lg overflow-hidden border-2 border-gray-200 mb-4">
                    <Map
                        mapId="5245ef91826603b0d1ffedbd"
                        defaultCenter={mapCenter}
                        defaultZoom={6}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        zoomControl={true}
                    >
                        {sortedPractices.map((practice) => (
                            <AdvancedMarker
                                key={practice.id}
                                position={practice._geoloc}
                                onClick={() => {
                                    handleSelectPractice(practice)
                                }}
                            >
                                {selectedPractice?.id === practice.id ? <img className={"map-pin-active"} alt={""}
                                                                             src={`${import.meta.env.PROD ? `https://${import.meta.env.BASE_URL}` : ''}/assets/markers/active-marker.png`}
                                                                             width={50}/> : <img alt={""}
                                                                                                 src={`${import.meta.env.PROD ? `https://${import.meta.env.BASE_URL}` : ''}/assets/markers/idle-marker.png`}
                                                                                                 width={32}/>}

                            </AdvancedMarker>
                        ))}
                    </Map>
                </div>
            )}

            {/* Practice List */}
            {viewMode === 'list' && (
                <div className="space-y-3 md:max-h-[500px] md:overflow-y-auto pr-2 mb-4">
                    {sortedPractices.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No practices found. Try searching for a location.</p>
                        </div>
                    ) : (
                        sortedPractices.map((practice) => (
                            <div
                                key={practice.id}
                                onClick={() => handleSelectPractice(practice)}
                                className={clsx(
                                    "relative border-2 rounded-lg p-3 cursor-pointer transition-all hover:shadow-md",
                                    selectedPractice?.id === practice.id
                                        ? "border-green-600 bg-green-50"
                                        : "border-gray-200 hover:border-blue-400"
                                )}
                            >
                                {selectedPractice?.id === practice.id && (
                                    <div className="absolute top-2 right-2">
                                        <CheckCircle className="text-green-600" size={20}/>
                                    </div>
                                )}

                                <div className="pr-7">
                                    <h3 className="font-semibold text-base mb-1">{practice.name}</h3>
                                    <div className="flex items-start gap-1 text-xs text-gray-600 mb-1">
                                        <MapPin size={14} className="mt-0.5 flex-shrink-0"/>
                                        <span>{practice.address}</span>
                                    </div>
                                    {practice.phone_formatted && (
                                        <p className="text-xs text-gray-500">
                                            {practice.phone_formatted}
                                        </p>
                                    )}
                                    {userLocation && practice._rankingInfo?.geoDistance && (
                                        <p className="text-xs text-blue-600 font-medium mt-1">
                                            {practice._rankingInfo.geoDistance.toFixed(1)} mi away
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {selectedPractice && (
                <div
                    className="mt-4 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                        <p className="text-sm font-medium text-green-800">Selected Practice:</p>
                        <p className="text-sm text-gray-700">{selectedPractice.name}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSelectPractice(null)}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
};

export default PracticeSelector;